import { Check, CheckCheck, ChevronDown, Tag, Trash2 } from 'lucide-react';
import { PopoverClose, PopoverPortal } from '@radix-ui/react-popover';
import { Popover, PopoverContent, PopoverTrigger } from 'src/shared/components/ui/popover';
import {
  Command,
  CommandGroup,
  CommandEmpty,
  CommandInput,
  CommandList,
  CommandItem,
} from 'src/shared/components/ui/command';
import WithTooltip from 'src/shared/components/utils/WithTooltip';
import TagHierarchy from 'src/shared/components/typography/TagHierarchy';
import { getIconForModel } from 'src/shared/lib/model.utils';
import { useModels } from 'src/features/models/queries/useModels';
import { useMutateMessage } from 'src/features/messages/queries/useMutateMessage';
import { ActualsUpdateType } from 'src/features/messages/types/message.types';
import { cn } from 'src/shared/lib/utils';
import DynamicIcon from 'src/shared/components/ui/icons/dynamic-icon';
import { useAuth } from 'src/features/auth/contexts/AuthContext';

type ModelValueRowProps = {
  integrationId: string;
  threadId: string;
  modelId: string;
  values?: string[];
  isVerified: boolean;
};

export default function ModelValueRow({ integrationId, threadId, modelId, values, isVerified }: ModelValueRowProps) {
  const { user } = useAuth();
  const { data: models } = useModels();
  const { updateMessage } = useMutateMessage();

  const model = models?.find((model) => model.id === modelId);

  function handleVerify(internalId: string) {
    handleChange(internalId);
  }

  function handleRemove() {
    handleChange(null);
  }

  function handleChange(newInternalId: string | null) {
    const currentValues = Array.isArray(values) ? [...values] : [];
    let action: ActualsUpdateType = values?.length ? (values.includes(newInternalId) ? 'VERIFY' : 'UPDATE') : 'ADD';
    let nextValues: string[] = currentValues;

    if (newInternalId === null) {
      action = 'REMOVE';
      nextValues = [];
    } else if (action === 'UPDATE') {
      // Replace the old value with the new one
      nextValues = [newInternalId];
    } else if (action === 'ADD') {
      // Add the new value
      nextValues = [newInternalId];
    } else if (action === 'VERIFY') {
      // Keep the same values for verify
      nextValues = currentValues;
    }

    updateMessage.mutate({
      integrationId,
      threadId,
      modelId,
      facetValues: nextValues,
      meta: {
        type: 'TAG',
        verified: action !== 'REMOVE',
        updates: [
          {
            action: action,
            oldValues: values?.[0] || '',
            newValues: newInternalId || '',
            user: {
              id: user?.sub,
              name: user?.name,
              email: user?.email,
            },
            timestamp: new Date().toISOString(),
          },
        ],
      },
    });
  }

  return (
    <Popover key={modelId}>
      <div className="flex items-center flex-wrap overflow-hidden gap-1 py-3 px-4 border bg-white w-full rounded-sm truncate">
        <div className="grow">
          <div className="text-typography-grey truncate pb-2 text-left leading-none">{model?.displayName}</div>

          <div className="flex items-center gap-1 h-7">
            <div
              data-verified={isVerified}
              className="flex-center shrink-0 rounded-sm w-7 h-full bg-dashboardblue-50 data-[verified=true]:bg-success-400/20"
            >
              <DynamicIcon
                name={getIconForModel({ id: modelId, isNer: model?.isNer })}
                size={14}
                className={cn('shrink-0 stroke-2 text-primary', isVerified && 'text-success-600')}
              />
            </div>
            <PopoverTrigger className="group/trigger flex w-full px-2 py-1 items-center gap-0.5 overflow-hidden rounded-sm hover:bg-neutral-100 transition-all hover:text-typography-black">
              <span data-empty={!values} className="truncate data-[empty=true]:text-typography-grey font-medium">
                {values
                  ? model?.tags.find((tag) => tag.internalId === values[0])?.shortName || values[0]
                  : 'Manually set tag'}
              </span>
              <ChevronDown
                size={16}
                className="ml-auto shrink-0 text-typography-grey group-hover/trigger:text-typography-black"
              />
            </PopoverTrigger>
          </div>
        </div>

        <div className="flex-center mt-auto shrink-0">
          {values && (
            <>
              {isVerified ? (
                <div className="h-7 mr-1 min-w-7 px-2 group flex-center rounded-sm  bg-success-400/20  text-success-700 transition-all">
                  <span className="pl-1 mr-2">Verified</span>
                  <CheckCheck size={16} />
                </div>
              ) : (
                <WithTooltip tooltip="Mark as correct">
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      handleVerify(values[0]);
                    }}
                    className="cursor-pointer size-7 group flex-center rounded-sm hover:bg-success-400/20 hover:text-success-600 transition-all text-typography-grey"
                  >
                    <CheckCheck size={16} />
                  </div>
                </WithTooltip>
              )}

              <WithTooltip delay={500} tooltip="Remove this tag" align="end">
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove();
                  }}
                  className="size-7 flex-center rounded-sm cursor-pointer hover:bg-destructive-50 hover:text-destructive transition-all text-typography-grey"
                >
                  <Trash2 size={16} />
                </div>
              </WithTooltip>
            </>
          )}
        </div>
      </div>
      <PopoverPortal>
        <PopoverContent className="w-same-as-trigger p-0 max-h-popover overflow-hidden">
          <Command>
            <CommandInput placeholder="Search tags..." className="shrink-0" />
            <CommandList>
              <CommandEmpty>No tags found</CommandEmpty>
            </CommandList>
            <CommandList>
              <CommandGroup className="overflow-y-auto max-h-[260px] overflow-x-hidden">
                {model?.tags
                  .sort((a, b) => a.hierarchy.join('').localeCompare(b.hierarchy.join('')))
                  .map((tag) => {
                    const isSelected = values?.includes(tag.internalId);
                    return (
                      <CommandItem
                        key={tag.id}
                        value={tag.hierarchy.join('')}
                        onSelect={() => handleChange(tag.internalId)}
                        className="group flex items-center truncate"
                      >
                        <PopoverClose className="w-full px-1 flex items-center gap-1">
                          <Tag
                            size={14}
                            data-selected={isSelected}
                            className="text-typography-grey mr-2 shrink-0 group-hover:text-typography-black data-[selected=true]:text-typography-black"
                          />
                          <TagHierarchy hierarchy={tag.hierarchy} />
                          <Check
                            size={16}
                            data-selected={isSelected}
                            className="ml-auto shrink-0 text-typography-black opacity-0 data-[selected=true]:opacity-100"
                          />{' '}
                        </PopoverClose>
                      </CommandItem>
                    );
                  })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
}
